/**
 * Resize an image file client-side using canvas.
 * Returns a resized File that preserves the original format for PNG/SVG,
 * converts everything else to JPEG for smaller size.
 */
export async function resizeImage(
  file: File,
  maxDimension: number = 1200
): Promise<File> {
  // Skip non-image files
  if (!file.type.startsWith("image/")) return file;

  // If file is already small enough (<100KB), skip resize
  if (file.size < 100 * 1024) return file;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      const { width, height } = img;

      // If already within bounds, return original
      if (width <= maxDimension && height <= maxDimension) {
        resolve(file);
        return;
      }

      // Calculate new dimensions preserving aspect ratio
      const ratio = Math.min(maxDimension / width, maxDimension / height);
      const newWidth = Math.round(width * ratio);
      const newHeight = Math.round(height * ratio);

      const canvas = document.createElement("canvas");
      canvas.width = newWidth;
      canvas.height = newHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // PNG stays PNG (transparency), everything else becomes JPEG
      const isPng = file.type === "image/png";
      const outputType = isPng ? "image/png" : "image/jpeg";
      const quality = isPng ? undefined : 0.85;

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Failed to create blob from canvas"));
            return;
          }

          const ext = isPng ? ".png" : ".jpg";
          const name = file.name.replace(/\.[^.]+$/, ext);
          resolve(new File([blob], name, { type: outputType }));
        },
        outputType,
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      // On error, return original file rather than failing
      resolve(file);
    };

    img.src = url;
  });
}
